import { faAws } from "@fortawesome/free-brands-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Cloud, TextCursorInput } from "lucide-react";
import { SiAnthropic, SiOpenai } from "react-icons/si";

import { AzureIcon } from "@/components/icons/AzureIcon";
import { GoogleCloudIcon } from "@/components/icons/GoogleCloudIcon";
import { OpenRouterIcon } from "@/components/icons/OpenRouterIcon";
import { cn } from "@/lib/utils";

/**
 * The provider's own mark.
 *
 * Extracted from `ResourceDetailSheet` so the filter menus can show it too: a
 * dropdown listing "aws / azure / gcp" as bare words makes the reader translate
 * a string back into a brand they would have recognised instantly.
 *
 * Brand colours are deliberate literals here, not theme tokens. They are frozen
 * vendor marks and are allowed to clash with the canvas; the fallback is themed,
 * because it is not a brand.
 */
export function ProviderLogo({
  provider,
  className,
  label,
}: {
  provider: string | null | undefined;
  className?: string;
  /**
   * Accessible name. Pass it where the mark is the *only* statement of the
   * provider (a table row); leave it off where a text label sits beside it (a
   * filter menu), so the provider is not announced twice.
   */
  label?: string;
}) {
  const size = cn("h-4 w-4", className);
  // A decorative duplicate is hidden; a load-bearing one is named.
  //
  // `aria-hidden: false` is not redundant: FontAwesome marks every icon it
  // renders `aria-hidden="true"` by default, so the AWS and GCP marks stayed
  // out of the accessibility tree even when a caller named them, and the
  // label this component documents did nothing on exactly the providers that
  // use FontAwesome.
  const a11y = label
    ? ({ role: "img", "aria-label": label, "aria-hidden": false } as const)
    : ({ "aria-hidden": true } as const);
  switch ((provider || "").toLowerCase()) {
    case "aws":
      return (
        <FontAwesomeIcon
          icon={faAws}
          className={cn(size, "text-aws")}
          {...a11y}
        />
      );
    case "azure":
      return <AzureIcon className={cn(size, "text-azure")} {...a11y} />;
    case "gcp":
    case "google":
      return <GoogleCloudIcon className={size} {...a11y} />;
    // OpenAI and Anthropic have real marks in `react-icons/si`, which is already
    // a dependency -- the Sidebar has been drawing them all along. This file
    // used one shared `Sparkles` for all four AI vendors, differing only by
    // colour, so the same page showed the correct logo in the nav and a generic
    // glyph beside the credential. Colour alone does not separate them either:
    // it is the one channel a colour-blind reader does not get.
    case "openai":
      return <SiOpenai className={cn(size, "text-openai")} {...a11y} />;
    case "anthropic":
      return <SiAnthropic className={cn(size, "text-anthropic")} {...a11y} />;
    case "openrouter":
      return <OpenRouterIcon className={cn(size, "text-openrouter")} {...a11y} />;
    // Cursor genuinely has no mark in the bundled sets, so it gets a glyph
    // that at least says something about the product -- and, more importantly,
    // is not the same glyph as anything else here.
    case "cursor":
      return (
        <TextCursorInput className={cn(size, "text-muted-foreground")} {...a11y} />
      );
    default:
      return <Cloud className={cn(size, "text-muted-foreground")} {...a11y} />;
  }
}
